import { Injectable, EventEmitter } from '@angular/core';

import { environment as env } from '../../../environments/environment';

import { from, of, Observable, BehaviorSubject, combineLatest, throwError, zip } from 'rxjs';
import { tap, map, catchError, concatMap, shareReplay, first, mergeMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { faUnderline } from '@fortawesome/free-solid-svg-icons';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { LgvModel } from '../../data-models/longevity.model';

@Injectable({
    providedIn: 'root'
})
export class LgvClientAuthService {

    _ClientBearerToken: string = '';
    ClientBearerToken$ = new BehaviorSubject(this._ClientBearerToken);

    _ClientTokenState: string = 'Not_Set';
    ClientBearerTokenState$ = new BehaviorSubject(this._ClientTokenState);

    _ClientInfo: LgvModel.ClientInfo = null;
    ClientInfo$ = new BehaviorSubject(this._ClientInfo);

    _LastError: HttpErrorResponse = null;
    LastError$ = new BehaviorSubject(this._LastError);

    get HaveClientBearerToken(): boolean { return this.ClientBearerTokenState$ && this.ClientBearerTokenState$.value == 'Valid'; }
    get HaveClientBearerToken$(): Observable<boolean> { return this.ClientBearerTokenState$.pipe(map(x => x == 'Valid')) }

    get IsClient(): boolean { return this.ClientBearerTokenState$.value != 'Not_Set'; }
    get IsAuthenticated(): boolean { return this.ClientBearerTokenState$.value == 'Valid'; }
    get IsTestingBearerToken(): boolean { return this.ClientBearerTokenState$.value == 'TestingBT'; }

    get IsAuthenticated$(): Observable<boolean> { return this.ClientBearerTokenState$.pipe(map(x => x == 'Valid')) }



    get BTKey(): string { return 'Lgv_ClientBearerToken'; }
    get ClientBearerTokenLocalStorage(): string { return localStorage.getItem(this.BTKey); }
    set ClientBearerTokenLocalStorage(x: string) { localStorage.setItem(this.BTKey, x); }
    get HaveSavedClientBearerToken(): boolean { return this.ClientBearerTokenLocalStorage && this.ClientBearerTokenLocalStorage.length > 0; }

    get LastUrlEncodedCT_Key(): string { return 'Lgv_LastUrlEncodedCT'; }
    get LastUrlEncodedCT(): string { return localStorage.getItem(this.LastUrlEncodedCT_Key); }
    set LastUrlEncodedCT(x: string) { localStorage.setItem(this.LastUrlEncodedCT_Key, x); }

    constructor(private router: Router, private http: HttpClient) {
    }


    public AuthenticateClientToken(ct: string): Observable<ClientAuthResponse> {

        // Step 1: Encode client token for url
        let encodedCT = encodeURIComponent(ct);
        let model = {};
        let bldUrl = `${env.LgvApiBaseUrl}/auth/client?token=${encodedCT}`;
        this.LastUrlEncodedCT = encodedCT;

        //console.log('Testing Client Token - ' + bldUrl);

        // Step 2: prepare to get bearer token from client token
        return this.http.post<any>(bldUrl, model).pipe(first(),
            catchError(err => throwError(err)),
            mergeMap((result) => {

                // Step 3: test bearer token by getting client info
                if (!!result) {
                    //console.log('Have BT response from CT:');
                    //console.log(r1);

                    let bt = result.token;

                    let clientAuthResponse = {
                        clientToken: ct,
                        bearerToken: bt,
                        error: null,
                        clientInfo: null
                    };

                    return zip(
                        this.ValidateAndSetClientBearerTokenAsync(bt),
                        of(clientAuthResponse)
                    )
                }
                else {
                    of({ error: 'Unable to get bearer Token', clientToken: ct, bearerToken: null, ClientInfo: null })
                }
            }),
            mergeMap(([clientInfo, clientAuthResponse]) => {
                if (!!clientInfo) {
                    clientAuthResponse.clientInfo = clientInfo;
                }
                else {
                    clientAuthResponse.error = 'Unable to get client infor with bearer token';
                }
                return of(clientAuthResponse);
            }));
    }

    public ValidateAndSetClientBearerTokenAsync(bt: string): Observable<LgvModel.ClientInfo> {

        this.ClientBearerTokenState$.next('TestingBT');
        this.ClientBearerToken$.next(bt);

        let bldUrl = `${env.LgvApiBaseUrl}/user/client`;

        //console.log('ValidatingClientBearerToken ASYNC...');
        return this.http.get<LgvModel.ClientInfo>(bldUrl).pipe(first(),
            tap(clientInfo => {
                if (clientInfo) {
                    //console.log('Have valid BT / ClientInfo in ValidateClientBearerToken:');
                    this.ClientInfo$.next(clientInfo);
                    this.ClientBearerTokenState$.next('Valid');
                    this.ClientBearerTokenLocalStorage = bt;
                } else {
                    console.warn('Client info is null or undefined.');
                    this.ClientBearerTokenState$.next('Not_Set');
                }
            }),
            catchError(err => {
                console.error('Error validating token:', err);
                this.ClientBearerTokenState$.next('Not_Set');
                // Return an empty observable, or rethrow depending on how caller handles it
                return throwError(() => err);  // or: return of(null); to complete gracefully
            })
        );
    }

    public CheckForSavedBearerToken() {

        if (!this.HaveSavedClientBearerToken) {
            return;
        }

        //console.log('Found bt in Local Storage - ');
        this.ValidateAndSetClientBearerTokenAsync(this.ClientBearerTokenLocalStorage).pipe(first()).subscribe();
    }


    logout() {
        this.ClientBearerTokenState$.next('Not_Set');
        localStorage.removeItem(this.BTKey)
        this.router.navigate(['/default/client-overview']);
    }

    ClearBTOnlyFromLocalStorage() {
        localStorage.removeItem(this.BTKey);
    }
}

export class ClientAuthResponse {
    clientToken: string;
    bearerToken: string;
    error?: any;
    clientInfo?: LgvModel.ClientInfo;
}
